import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography,
  Alert,
} from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { QuestionInput, QuestionType, QuestionWithAnswer } from '../../types/question'

interface QuestionEditDialogProps {
  open: boolean
  question: QuestionWithAnswer | null // null = 新規作成
  onClose: () => void
  onSave: (data: QuestionInput) => Promise<void>
  hasTestResults?: boolean
}

const defaultFormData: QuestionInput = {
  questionText: '',
  questionType: 'SINGLE_CHOICE',
  options: ['', ''],
  correctAnswer: '0',
  explanation: '',
  points: 10,
}

export const QuestionEditDialog: React.FC<QuestionEditDialogProps> = ({
  open,
  question,
  onClose,
  onSave,
  hasTestResults = false,
}) => {
  const { t } = useTranslation('test')
  const [formData, setFormData] = useState<QuestionInput>(defaultFormData)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEditMode = !!question

  useEffect(() => {
    if (open) {
      if (question) {
        setFormData({
          questionText: question.questionText,
          questionType: question.questionType,
          options: question.options.length > 0 ? question.options : ['', ''],
          correctAnswer: question.correctAnswer,
          explanation: question.explanation || '',
          points: question.points,
        })
      } else {
        setFormData(defaultFormData)
      }
      setError(null)
    }
  }, [open, question])

  const handleQuestionTypeChange = (newType: QuestionType) => {
    setFormData((prev) => {
      let newOptions = prev.options
      let newCorrectAnswer = prev.correctAnswer

      if (newType === 'TRUE_FALSE') {
        newOptions = [
          t('question.form.trueOption', '正しい'),
          t('question.form.falseOption', '誤り'),
        ]
        newCorrectAnswer = '0'
      } else if (newType === 'ESSAY') {
        newOptions = []
        newCorrectAnswer = ''
      } else if (prev.questionType === 'ESSAY' || prev.questionType === 'TRUE_FALSE') {
        newOptions = ['', '']
        newCorrectAnswer = '0'
      }

      return {
        ...prev,
        questionType: newType,
        options: newOptions,
        correctAnswer: newCorrectAnswer,
      }
    })
  }

  const handleOptionChange = (index: number, value: string) => {
    setFormData((prev) => {
      const newOptions = [...prev.options]
      newOptions[index] = value
      return { ...prev, options: newOptions }
    })
  }

  const handleAddOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, ''],
    }))
  }

  const handleRemoveOption = (index: number) => {
    setFormData((prev) => {
      const newOptions = prev.options.filter((_, i) => i !== index)
      let newCorrectAnswer = prev.correctAnswer

      // 正解の調整
      if (prev.questionType === 'SINGLE_CHOICE') {
        const correctIndex = parseInt(prev.correctAnswer)
        if (index === correctIndex) {
          newCorrectAnswer = '0'
        } else if (index < correctIndex) {
          newCorrectAnswer = String(correctIndex - 1)
        }
      } else if (prev.questionType === 'MULTIPLE_CHOICE') {
        const correctIndices = prev.correctAnswer.split(',').map(Number)
        const newCorrectIndices = correctIndices
          .filter((i) => i !== index)
          .map((i) => (i > index ? i - 1 : i))
        newCorrectAnswer = newCorrectIndices.join(',') || '0'
      }

      return { ...prev, options: newOptions, correctAnswer: newCorrectAnswer }
    })
  }

  const handleCorrectAnswerChange = (value: string) => {
    setFormData((prev) => ({ ...prev, correctAnswer: value }))
  }

  const handleMultipleCorrectToggle = (index: number) => {
    setFormData((prev) => {
      const currentIndices = prev.correctAnswer ? prev.correctAnswer.split(',').map(Number) : []
      let newIndices: number[]

      if (currentIndices.includes(index)) {
        newIndices = currentIndices.filter((i) => i !== index)
      } else {
        newIndices = [...currentIndices, index].sort((a, b) => a - b)
      }

      return {
        ...prev,
        correctAnswer: newIndices.length > 0 ? newIndices.join(',') : '0',
      }
    })
  }

  const validateForm = (): boolean => {
    if (!formData.questionText.trim()) {
      setError(t('question.validation.questionTextRequired', '問題文は必須です'))
      return false
    }

    if (['SINGLE_CHOICE', 'MULTIPLE_CHOICE'].includes(formData.questionType)) {
      if (formData.options.length < 2) {
        setError(t('question.validation.optionsRequired', '選択肢は2つ以上必要です'))
        return false
      }
      if (formData.options.some((opt) => !opt.trim())) {
        setError(t('question.validation.optionsEmpty', '空の選択肢があります'))
        return false
      }
    }

    if (!formData.correctAnswer && formData.questionType !== 'ESSAY') {
      setError(t('question.validation.correctAnswerRequired', '正解は必須です'))
      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setSaving(true)
    setError(null)

    try {
      await onSave(formData)
      onClose()
    } catch (err: any) {
      setError(err.message || t('question.error.saveFailed', '保存に失敗しました'))
    } finally {
      setSaving(false)
    }
  }

  const showOptions = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TRUE_FALSE'].includes(
    formData.questionType
  )
  const canEditOptions = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE'].includes(formData.questionType)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? t('question.edit', '問題を編集') : t('question.add', '問題を追加')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {hasTestResults && (
            <Alert severity="warning">
              {t(
                'question.hasResultsWarning',
                'この問題には既存の回答履歴があります。変更は新しい受講にのみ反映されます。'
              )}
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {/* 問題文 */}
          <TextField
            label={t('question.form.questionText', '問題文')}
            value={formData.questionText}
            onChange={(e) => setFormData((prev) => ({ ...prev, questionText: e.target.value }))}
            multiline
            rows={3}
            fullWidth
            required
          />

          {/* 問題タイプ */}
          <FormControl fullWidth>
            <InputLabel>{t('question.form.questionType', '問題タイプ')}</InputLabel>
            <Select
              value={formData.questionType}
              label={t('question.form.questionType', '問題タイプ')}
              onChange={(e) => handleQuestionTypeChange(e.target.value as QuestionType)}
            >
              <MenuItem value="SINGLE_CHOICE">
                {t('detail.types.SINGLE_CHOICE', '単一選択')}
              </MenuItem>
              <MenuItem value="MULTIPLE_CHOICE">
                {t('detail.types.MULTIPLE_CHOICE', '複数選択')}
              </MenuItem>
              <MenuItem value="TRUE_FALSE">{t('detail.types.TRUE_FALSE', '真偽')}</MenuItem>
              <MenuItem value="ESSAY">{t('detail.types.ESSAY', '記述')}</MenuItem>
            </Select>
          </FormControl>

          {/* 選択肢 */}
          {showOptions && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('question.form.options', '選択肢')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {formData.options.map((option, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ minWidth: 24 }}>
                      {index + 1}.
                    </Typography>
                    <TextField
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!canEditOptions}
                    />
                    {canEditOptions && formData.options.length > 2 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveOption(index)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {canEditOptions && (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddOption}
                    size="small"
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {t('question.form.addOption', '選択肢を追加')}
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* 正解 */}
          {formData.questionType !== 'ESSAY' && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('question.form.correctAnswer', '正解')}
              </Typography>
              {formData.questionType === 'SINGLE_CHOICE' ||
              formData.questionType === 'TRUE_FALSE' ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.correctAnswer}
                    onChange={(e) => handleCorrectAnswerChange(e.target.value)}
                  >
                    {formData.options.map((option, index) => (
                      <MenuItem key={index} value={String(index)}>
                        {index + 1}.{' '}
                        {option || `${t('question.form.option', '選択肢')} ${index + 1}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : formData.questionType === 'MULTIPLE_CHOICE' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {formData.options.map((option, index) => {
                    const isCorrect = formData.correctAnswer.split(',').map(Number).includes(index)
                    return (
                      <Button
                        key={index}
                        variant={isCorrect ? 'contained' : 'outlined'}
                        color={isCorrect ? 'success' : 'inherit'}
                        onClick={() => handleMultipleCorrectToggle(index)}
                        size="small"
                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      >
                        {index + 1}.{' '}
                        {option || `${t('question.form.option', '選択肢')} ${index + 1}`}
                      </Button>
                    )
                  })}
                  <Typography variant="caption" color="textSecondary">
                    {t('question.form.multipleCorrectHelp', '複数の正解を選択できます')}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          )}

          {/* 解説 */}
          <TextField
            label={t('question.form.explanation', '解説（任意）')}
            value={formData.explanation}
            onChange={(e) => setFormData((prev) => ({ ...prev, explanation: e.target.value }))}
            multiline
            rows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('form.cancel', 'キャンセル')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? t('form.saving', '保存中...') : t('form.save', '保存')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default QuestionEditDialog
